/**
 * useContentDescription Hook
 *
 * Manages state for the content description step: text input, hashtag
 * suggestions (Firestore + local favourites), and mention integration.
 *
 * Mirrors Flutter's SetContentDescriptionScreenState from
 * set_content_description_screen.dart.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PageCursor } from '@/data/types/json';
import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';
import { useMentions } from '@/presentation/hooks/useMentions';
import { SearchUsersSheet } from '@/presentation/feature/content/components/sheets/searchUsers/SearchUsersSheet';
import { SearchContentTagsUseCase } from '@/domain/usecases/search/searchContentTagsUseCase';
import { GetFavouriteHashTagsUseCase } from '@/domain/usecases/publish/getFavouriteHashTagsUseCase';
import { ProcessContentDescriptionUseCase } from '@/domain/usecases/publish/processContentDescriptionUseCase';
import { SaveFavouriteHashTagsUseCase } from '@/domain/usecases/publish/saveFavouriteHashTagsUseCase';
import { parseHashtagAtCursor, insertHashtag, type HashtagAtCursor } from '@/domain/utils/hashtagsParser';
import { useDebounce } from '@/presentation/hooks/useDebounce';
import { COMMON_CONSTANTS } from '@/presentation/constants/commonConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import type { DescriptionResult } from '@/domain/entities/publish/descriptionResult';

export type { DescriptionResult };

interface UseContentDescriptionParams {
  screenKey?: string;
}

export function useContentDescription({ screenKey }: UseContentDescriptionParams = {}) {
  const [description, setDescription] = useState('');
  const [isEditingHashTags, setIsEditingHashTags] = useState(false);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<ContentTagEntity[]>([]);
  const [isFetchingHashTags, setIsFetchingHashTags] = useState(false);
  const [hasMoreHashTags, setHasMoreHashTags] = useState(true);
  const [hashtagQuery, setHashtagQuery] = useState('');
  const debouncedHashtagQuery = useDebounce(hashtagQuery, COMMON_CONSTANTS.searchTextDebounce);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const hashtagRef = useRef<HashtagAtCursor | null>(null);
  const lastFetchedRef = useRef<PageCursor | undefined>(undefined);
  const currentHashtagQueryRef = useRef<string>('');
  const isFetchingRef = useRef(false);

  const searchUseCase = new SearchContentTagsUseCase();
  const getFavouritesUseCase = new GetFavouriteHashTagsUseCase();
  const processUseCase = new ProcessContentDescriptionUseCase();
  const saveFavouritesUseCase = new SaveFavouriteHashTagsUseCase();

  // Mentions integration (same pattern as CommentInput)
  const selectUser = useCallback(
    () => SearchUsersSheet.showForMention(undefined, screenKey),
    [screenKey],
  );
  const { handleChange: mentionHandleChange, isEditingMentions } = useMentions({ selectUser });

  const resetHashtagsState = useCallback(() => {
    setIsEditingHashTags(false);
    setHashtagSuggestions([]);
    setIsFetchingHashTags(false);
    isFetchingRef.current = false;
    setHasMoreHashTags(true);
    setHashtagQuery('');
    hashtagRef.current = null;
    lastFetchedRef.current = undefined;
    currentHashtagQueryRef.current = '';
  }, []);

  const fetchHashTags = useCallback(async (prefix: string, getFreshData: boolean) => {
    if (isFetchingRef.current || !prefix) return;

    isFetchingRef.current = true;
    setIsFetchingHashTags(true);

    try {
      const result = await searchUseCase.execute(
        prefix,
        getFreshData ? undefined : lastFetchedRef.current,
      );

      if (result.contentTags.length === 0 && getFreshData) {
        // New hashtag — show a placeholder entry
        const newTag: ContentTagEntity = { name: prefix, uses: 0, localUses: 0 };
        setHashtagSuggestions([newTag]);
      } else if (getFreshData) {
        setHashtagSuggestions(result.contentTags);
      } else {
        setHashtagSuggestions(prev => [...prev, ...result.contentTags]);
      }

      lastFetchedRef.current = result.cursor ?? undefined;
      setHasMoreHashTags(result.hasMore);
    } catch (error) {
      appLogger.error('Failed to fetch hashtags', 'useContentDescription.fetchHashTags', { error: error as Error });
    } finally {
      isFetchingRef.current = false;
      setIsFetchingHashTags(false);
    }
  }, []);

  const loadFavouriteHashTags = useCallback(async () => {
    try {
      const favourites = await getFavouritesUseCase.execute();
      setHashtagSuggestions(favourites);
    } catch (error) {
      appLogger.error('Failed to load favourite hashtags', 'useContentDescription.loadFavouriteHashTags', { error: error as Error });
    }
  }, []);

  const handleDescriptionChange = useCallback((
    newValue: string,
    cursorPos: number,
    setValue: (text: string, cursorPos: number) => void,
  ) => {
    // Check for mentions first (delegates to useMentions)
    const mentionHandled = mentionHandleChange(newValue, cursorPos, setValue);

    // Check for hashtags
    const atCursor = parseHashtagAtCursor(newValue, cursorPos);

    if (!atCursor) {
      resetHashtagsState();
      if (!mentionHandled) {
        setDescription(newValue);
      }
      return mentionHandled;
    }

    // Hashtag detected
    setIsEditingHashTags(true);
    hashtagRef.current = atCursor;

    if (!mentionHandled) {
      setDescription(newValue);
    }

    const query = atCursor.hashtag;
    currentHashtagQueryRef.current = query;

    if (query === '') {
      // Just '#' typed — show local favourites immediately
      setHashtagQuery('');
      lastFetchedRef.current = undefined;
      setHasMoreHashTags(true);
      void loadFavouriteHashTags();
    } else {
      setHashtagQuery(query);
    }

    return mentionHandled;
  }, [mentionHandleChange, resetHashtagsState, loadFavouriteHashTags]);

  useEffect(() => {
    if (!debouncedHashtagQuery) return;
    lastFetchedRef.current = undefined;
    setHasMoreHashTags(true);
    void fetchHashTags(debouncedHashtagQuery, true);
  }, [debouncedHashtagQuery, fetchHashTags]);

  const selectHashtag = useCallback((tag: ContentTagEntity) => {
    const current = hashtagRef.current;
    if (!current) return;

    const { text, cursorPos } = insertHashtag(description, current.startPos, current.endPos, tag.name);
    setDescription(text);
    resetHashtagsState();

    // Restore cursor position
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        try {
          el.setSelectionRange(cursorPos, cursorPos);
        } catch {
          // ignore
        }
      }
    });
  }, [description, resetHashtagsState]);

  const setValueAndCursor = useCallback((text: string, cursorPos: number) => {
    setDescription(text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        try {
          el.setSelectionRange(cursorPos, cursorPos);
        } catch {
          // ignore
        }
      }
    });
  }, []);

  const insertHashtagSymbol = useCallback(() => {
    if (description.length >= InputLengthFormatters.contentDescriptionMaxLength) return;

    const el = inputRef.current;
    const cursor = el?.selectionStart ?? description.length;
    const next = `${description.slice(0, cursor)}#${description.slice(cursor)}`;
    const nextCursor = cursor + 1;

    setDescription(next);
    handleDescriptionChange(next, nextCursor, setValueAndCursor);

    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(nextCursor, nextCursor); } catch { /* ignore */ }
    });
  }, [description, handleDescriptionChange, setValueAndCursor]);

  const insertMentionSymbol = useCallback(() => {
    if (description.length >= InputLengthFormatters.contentDescriptionMaxLength) return;

    const el = inputRef.current;
    const cursor = el?.selectionStart ?? description.length;
    const next = `${description.slice(0, cursor)}@${description.slice(cursor)}`;
    const nextCursor = cursor + 1;

    const handled = mentionHandleChange(next, nextCursor, setValueAndCursor);
    if (!handled) setDescription(next);

    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(nextCursor, nextCursor); } catch { /* ignore */ }
    });
  }, [description, mentionHandleChange, setValueAndCursor]);

  const handleFocusChange = useCallback((focused: boolean) => {
    if (!focused) {
      setIsEditingHashTags(false);
    }
  }, []);

  const loadMoreHashTags = useCallback(() => {
    if (!hasMoreHashTags || isFetchingRef.current) return;
    const query = currentHashtagQueryRef.current;
    if (query) {
      void fetchHashTags(query, false);
    }
  }, [hasMoreHashTags, fetchHashTags]);

  const processAndFinish = useCallback((): DescriptionResult => {
    const result = processUseCase.execute(description);

    // Fire-and-forget: save favourite hashtags for next time
    if (result.hashTagsObjects.length > 0) {
      void saveFavouritesUseCase.execute(result.hashTagsObjects);
    }

    return {
      description: result.processedDescription,
      hashTags: result.hashTags,
      hashTagsObjects: result.hashTagsObjects,
      mentions: result.mentions,
    };
  }, [description]);

  return {
    description,
    inputRef,
    isEditingHashTags,
    hashtagSuggestions,
    isFetchingHashTags,
    isEditingMentions,
    handleDescriptionChange,
    selectHashtag,
    insertHashtagSymbol,
    insertMentionSymbol,
    handleFocusChange,
    loadMoreHashTags,
    processAndFinish,
    setValueAndCursor,
  };
}
